/**
 * Pure decisions for mobile audio recovery.
 * The Jitsi page script mirrors the track rules in
 * jitsi-docker/branding/mobile-audio-recovery.js.
 */

export const RECOVERY_STATUS = {
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
  RESTORING: "restoring",
  INTERRUPTED: "interrupted",
  MIC_UNAVAILABLE: "mic-unavailable",
  RECONNECTED: "reconnected",
};

export const MESSAGE_SOURCE = "biz-meeting-audio";
export const MESSAGE_VERSION = 1;

export function createSessionId(now = Date.now, random = Math.random) {
  const clock = typeof now === "function" ? now() : Date.now();
  const rand = typeof random === "function" ? random() : Math.random();
  return `bm-${clock.toString(36)}-${String(rand).slice(2, 8)}`;
}

export function isMobileMeetingBrowser(userAgent, platform, maxTouchPoints) {
  const agent = String(userAgent || "");
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(agent)) return true;
  return platform === "MacIntel" && Number(maxTouchPoints) > 1;
}

export function isJitsiMessageOrigin(origin, domain) {
  try {
    const expectedHost = String(domain || "")
      .replace(/^https?:\/\//, "")
      .replace(/\/.*$/, "");
    if (!expectedHost) return false;
    return new URL(origin).host === expectedHost;
  } catch (err) {
    return false;
  }
}

export function isMicPermissionError(name) {
  const value = String(name || "").toLowerCase();
  return (
    value.includes("permission") ||
    value.includes("notallowed") ||
    value.includes("not-allowed") ||
    value.includes("security")
  );
}

export function isDeviceMissingError(name) {
  const value = String(name || "").toLowerCase();
  return (
    value.includes("notfound") ||
    value.includes("not_found") ||
    value.includes("not-found") ||
    value.includes("overconstrained") ||
    value.includes("constraint")
  );
}

/**
 * Replace the microphone only after a real interruption signal.
 * A live track that the user muted on purpose is left alone.
 * An ended or browser-muted track is replaced, and the caller must
 * re-apply an intentional mute on the new track.
 */
export function shouldReplaceAudioTrack(audio, interruptionLikely) {
  if (!interruptionLikely) return false;
  if (!audio || !audio.present) return true;
  if (audio.readyState === "ended") return true;
  if (audio.browserMuted) return true;
  return false;
}

/**
 * Never turn the camera back on when the user switched it off.
 * Only replace a camera that was on and then ended or was muted by the OS.
 */
export function shouldReplaceVideoTrack(video, interruptionLikely) {
  if (!interruptionLikely || !video) return false;
  if (video.userMuted) return false;
  if (!video.present) return false;
  if (video.readyState === "ended") return true;
  if (video.browserMuted) return true;
  return false;
}

export function pickInputDevice(devices, kind) {
  const empty = { label: "", deviceId: "" };
  if (!devices) return empty;
  const key =
    kind === "videoinput" ? "videoInput" : kind === "audiooutput" ? "audioOutput" : "audioInput";
  let device = null;
  if (Array.isArray(devices)) {
    device = devices.find((item) => item && (item.kind === kind || item.kind === key)) || null;
  } else if (typeof devices === "object") {
    device = devices[key] || devices[kind] || null;
  }
  if (!device || typeof device !== "object") return empty;
  return {
    label: device.label || device.deviceLabel || "",
    deviceId: device.deviceId || device.id || "",
  };
}

export function bannerCopy(status) {
  switch (status) {
    case RECOVERY_STATUS.RECONNECTING:
      return {
        title: "Reconnecting",
        body: "The meeting connection dropped. Wait a moment, or rejoin if it does not return.",
      };
    case RECOVERY_STATUS.RESTORING:
      return {
        title: "Restoring audio",
        body: "The meeting is still open. Restoring the microphone and speaker.",
      };
    case RECOVERY_STATUS.INTERRUPTED:
      return {
        title: "Audio interrupted",
        body: "You are still in the meeting, but sound stopped. Tap Restore audio. If you still cannot hear others, rejoin.",
      };
    case RECOVERY_STATUS.MIC_UNAVAILABLE:
      return {
        title: "Microphone unavailable",
        body: "The microphone cannot be used right now. Allow microphone access for this site, then tap Restore audio.",
      };
    case RECOVERY_STATUS.RECONNECTED:
      return {
        title: "Reconnected",
        body: "Audio is restored. Your mute and camera choices were kept.",
      };
    default:
      return { title: "Connected", body: "" };
  }
}
